import React from 'react'
import styles from './PostManageEdit.module.scss'
import { useNavigate } from 'react-router-dom'
import { useState, useEffect, useRef, useCallback } from 'react'
import { ArrowLeftIcon } from '@heroicons/react/24/solid'
import PreviewModal from '../../components/postItem/PreviewModal'
import axios from 'axios'
import DomesticContractInfoSubmitForm from '../jobpost/DomesticContractInfoSubmitForm'
import OverseasContractInfoSubmitForm from '../jobpost/OverseasContractInfoSubmitForm'
import GeneralContractInfoSubmitForm from '../jobpost/GeneralContractInfoSubmitForm'
import { getAccessTokenFromStorage } from '../../utils'

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT

const mapDomesticFormToApi = (fd) => ({
	isSalaryTaxIncluded: fd.applyTax !== 'exclude',
	payrollCycles: fd.payPeriod === 'weekly' ? 'weekly' : 'perGame',
	weeklySalaryCutThrough: Number(fd.cutPass || 0),
	weeklySalaryCutFail: Number(fd.cutOut || 0),
	jejuTournamentParticipation: fd.isTourJejudo,
	expenseReimbursementJeju: fd.isTourJejudo
		? Number(String(fd.jejudoSupport || '').replace(/,/g, ''))
		: 0,
	wearSponsoredHat: fd.sponsorCap,
	prizeType: fd.incentiveTaxOption === 'amountDisplayed' ? 'displayed' : 'actual',
	incentives: (fd.incentiveSettings || []).map((incentive) => ({
		startRank: incentive.start,
		endRank: incentive.end === 'CUT' ? 0 : incentive.end,
		percentage: incentive.percentage,
	})),
})

const mapOverseasFormToApi = (fo) => ({
	isSalaryTaxIncluded: fo.applyTax !== 'exclude',
	payrollCycles: fo.payPeriod === 'weekly' ? 'weekly' : 'perGame',
	weeklySalaryCutThrough: Number(fo.cutPass || 0),
	weeklySalaryCutFail: Number(fo.cutOut || 0),
	wearSponsoredHat: fo.sponsorCap,
	expenseReimbursement: fo.overseasSupport,
	prizeType: fo.incentiveTaxOption === 'amountDisplayed' ? 'displayed' : 'actual',
	incentives: (fo.incentiveSettings || []).map((incentive) => ({
		startRank: incentive.start,
		endRank: incentive.end === 'CUT' ? 0 : incentive.end,
		percentage: incentive.percentage,
	})),
})

const mapGeneralFormToApi = (g) => ({
	isSalaryTaxIncluded: g.applyTax !== 'exclude',
	payrollCycles: g.payPeriod === 'weekly' ? 'weekly' : 'perGame',
	salaryAmountForPeriod: Number(String(g.periodSalary || '').replace(/,/g, '')),
	isOverseasIncluded: g.overseasTour,
	expenseReimbursement: g.overseasTour ? g.overseasSupport : null,
	wearSponsoredHat: g.sponsorCap,
	prizeType: g.incentiveTaxOption === 'amountDisplayed' ? 'displayed' : 'actual',
	incentives: (g.incentiveSettings || []).map((incentive) => ({
		startRank: incentive.start,
		endRank: incentive.end,
		percentage: incentive.percentage,
	})),
})

const PostManageEdit = () => {
	const [itemData, setItemData] = useState(null)
	const [itemTitle, setItemTitle] = useState('')
	const [timePassed, setTimePassed] = useState(null)
	const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)
	const modalRef = useRef(null)
	const [isDragging, setIsDragging] = useState(false)
	const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
	const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 })
	const [hasBeenDragged, setHasBeenDragged] = useState(false)
	const navigate = useNavigate()
	const [jobPostTitle, setJobPostTitle] = useState('')
	const [jobPostBrief, setJobPostBrief] = useState('')
	const [isNextButtonAvailable, setIsNextButtonAvailable] = useState(false)
	const [jobPostTitleLength, setJobPostTitleLength] = useState(0)
	const [jobPostBriefLength, setJobPostBriefLength] = useState(0)
	const [initialJobPostTitle, setInitialJobPostTitle] = useState('')
	const [initialJobPostBrief, setInitialJobPostBrief] = useState('')
	const [isSuccessMessageOpen, setIsSuccessMessageOpen] = useState(false)
	const [detailLoading, setDetailLoading] = useState(true)
	const [contractFormsKey, setContractFormsKey] = useState(0)

	const domesticFormRef = useRef(null)
	const overseasFormRef = useRef(null)
	const generalFormRef = useRef(null)

	const noopValidation = useCallback(() => {}, [])

	const handleMouseDown = (e) => {
		if (modalRef.current) {
			const rect = modalRef.current.getBoundingClientRect()
			setDragOffset({
				x: e.clientX - rect.left,
				y: e.clientY - rect.top,
			})
			setIsDragging(true)
		}
	}

	const handleMouseMove = (e) => {
		if (isDragging && modalRef.current) {
			const newX = e.clientX - dragOffset.x
			const newY = e.clientY - dragOffset.y

			const maxX = window.innerWidth - modalRef.current.offsetWidth
			const maxY = window.innerHeight - modalRef.current.offsetHeight

			const clampedX = Math.max(0, Math.min(newX, maxX))
			const clampedY = Math.max(0, Math.min(newY, maxY))

			modalRef.current.style.transform = `translate(${clampedX}px, ${clampedY}px)`

			setModalPosition({ x: clampedX, y: clampedY })
			setHasBeenDragged(true)
		}
	}

	const handleMouseUp = () => {
		setIsDragging(false)
	}

	useEffect(() => {
		if (isDragging) {
			document.addEventListener('mousemove', handleMouseMove)
			document.addEventListener('mouseup', handleMouseUp)
			return () => {
				document.removeEventListener('mousemove', handleMouseMove)
				document.removeEventListener('mouseup', handleMouseUp)
			}
		}
	}, [isDragging, dragOffset])

	useEffect(() => {
		if (isPreviewModalOpen && modalRef.current) {
			let targetX, targetY

			if (hasBeenDragged) {
				targetX = modalPosition.x
				targetY = modalPosition.y
			} else {
				const modalWidth = 360
				const modalHeight = 640
				targetX = (window.innerWidth - modalWidth) / 2
				targetY = (window.innerHeight - modalHeight) / 2
			}

			modalRef.current.style.transform = `translate(${targetX}px, ${targetY}px)`
		}
	}, [isPreviewModalOpen, hasBeenDragged, modalPosition])

	useEffect(() => {
		return () => {
			setHasBeenDragged(false)
			setModalPosition({ x: 0, y: 0 })
		}
	}, [])

	useEffect(() => {
		const editPostItem = sessionStorage.getItem('editPostItem')
		const timePassedVal = sessionStorage.getItem('timePassed')
		if (!editPostItem) {
			setDetailLoading(false)
			return
		}
		try {
			const parsed = JSON.parse(editPostItem)
			setItemData(parsed)
			setTimePassed(timePassedVal)
		} catch {
			setDetailLoading(false)
		}
	}, [])

	useEffect(() => {
		if (!itemData?.id) {
			setDetailLoading(false)
			return
		}
		let cancelled = false
		const run = async () => {
			try {
				setDetailLoading(true)
				const res = await axios.get(`${API_ENDPOINT}/job-post/${itemData.id}`)
				if (cancelled) return
				const d = res.data
				setItemData(d)
				setJobPostTitle(d.subject || '')
				setJobPostBrief(d.content || '')
				setInitialJobPostTitle(d.subject || '')
				setInitialJobPostBrief(d.content || '')
				setJobPostTitleLength((d.subject || '').length)
				setJobPostBriefLength((d.content || '').length)
				if (d.domesticContract) {
					sessionStorage.setItem(
						'DomesticContractPreviousData',
						JSON.stringify(d.domesticContract),
					)
				} else {
					sessionStorage.removeItem('DomesticContractPreviousData')
				}
				if (d.overseasContract) {
					sessionStorage.setItem(
						'OverseasContractPreviousData',
						JSON.stringify(d.overseasContract),
					)
				} else {
					sessionStorage.removeItem('OverseasContractPreviousData')
				}
				if (d.termContract) {
					sessionStorage.setItem(
						'GeneralContractPreviousData',
						JSON.stringify(d.termContract),
					)
				} else {
					sessionStorage.removeItem('GeneralContractPreviousData')
				}
				setContractFormsKey((k) => k + 1)
			} catch (e) {
				console.error('공고 상세 로드 실패:', e)
			} finally {
				if (!cancelled) setDetailLoading(false)
			}
		}
		run()
		return () => {
			cancelled = true
		}
	}, [itemData?.id])

	useEffect(() => {
		let title = ''
		const subj = itemData?.subject
		if (itemData?.contractType === 'single') {
			title = '[단기계약] ' + subj
		} else if (itemData?.contractType === 'long_term') {
			title = '[장기계약] ' + subj
		} else if (itemData?.contractType === 'year_first_half') {
			title = '[기간계약(상반기)] ' + subj
		} else if (itemData?.contractType === 'year_second_half') {
			title = '[기간계약(하반기)] ' + subj
		} else if (itemData?.contractType === 'both') {
			title = '[기간계약(상반기/하반기)] ' + subj
		}
		setItemTitle(title)
	}, [itemData])

	useEffect(() => {
		if (detailLoading) {
			setIsNextButtonAvailable(false)
			return
		}
		const changed = jobPostTitle !== initialJobPostTitle || jobPostBrief !== initialJobPostBrief
		const canSubmitContract = itemData?.status === 'processing'
		setIsNextButtonAvailable(changed || canSubmitContract)
	}, [
		jobPostTitle,
		jobPostBrief,
		initialJobPostTitle,
		initialJobPostBrief,
		detailLoading,
		itemData?.status,
	])

	const buildContractPayload = () => {
		const contract = {}
		const ct = itemData?.contractType
		if (ct === 'single' || ct === 'long_term') {
			if (itemData?.domesticContract && domesticFormRef.current) {
				contract.domesticContract = mapDomesticFormToApi(
					domesticFormRef.current.getFormData(),
				)
			}
			if (itemData?.overseasContract && overseasFormRef.current) {
				contract.overseasContract = mapOverseasFormToApi(
					overseasFormRef.current.getFormData(),
				)
			}
		} else if (ct === 'year_first_half' || ct === 'year_second_half' || ct === 'both') {
			if (itemData?.termContract && generalFormRef.current) {
				contract.termContract = mapGeneralFormToApi(generalFormRef.current.getFormData())
			}
		}
		return contract
	}

	const onSubmit = async () => {
		if (!itemData?.id) return
		try {
			const payload = {
				postSubject: jobPostTitle,
				postContent: jobPostBrief,
			}
			if (itemData.status === 'processing') {
				const contract = buildContractPayload()
				if (
					contract.domesticContract ||
					contract.overseasContract ||
					contract.termContract
				) {
					payload.contract = contract
				}
			}
			const accessToken = getAccessTokenFromStorage()
			if (!accessToken) {
				alert('로그인이 필요합니다.')
				return
			}
			const response = await axios.patch(`${API_ENDPOINT}/job-post/${itemData.id}`, payload, {
				headers: {
					Authorization: 'Bearer ' + accessToken,
				},
			})
			if (response.status >= 200 && response.status < 300) {
				sessionStorage.removeItem('editPostItem')
				sessionStorage.removeItem('timePassed')
				setIsSuccessMessageOpen(true)
				setTimeout(() => {
					setIsSuccessMessageOpen(false)
					navigate(-1)
				}, 2000)
			} else {
				alert('공고 수정에 실패했습니다.')
			}
		} catch (error) {
			console.error('Error submitting job post:', error)
			const msg = error.response?.data?.message
			alert(msg || '공고 수정에 실패했습니다.')
		}
	}

	const formatNumber = (num) => {
		if (!num && num !== 0) return '0'
		return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
	}

	const payCycleLabel = (c) => {
		const p = c?.payrollCycles ?? c?.payRollCycles
		return p === 'weekly' ? '주급' : '일시불제'
	}

	const renderDisplayIcon = () => {
		const isDomestic = itemData?.domesticContract != null
		const isOverseas = itemData?.overseasContract != null

		if (itemData?.contractType === 'single') {
			return (
				<>
					<span className={styles.displayIcon}>단기계약</span>
					{isDomestic && (
						<span className={styles.displayIcon}>
							{payCycleLabel(itemData?.domesticContract)}
						</span>
					)}
					{!isDomestic && isOverseas && (
						<span className={styles.displayIcon}>
							{payCycleLabel(itemData?.overseasContract)}
						</span>
					)}
					{isDomestic && (
						<span className={styles.displayIcon}>
							{formatNumber(itemData?.domesticContract?.weeklySalaryCutThrough)}원 /{' '}
							{formatNumber(itemData?.domesticContract?.weeklySalaryCutFail)}원
						</span>
					)}
					{!isDomestic && isOverseas && (
						<span className={styles.displayIcon}>
							{formatNumber(itemData?.overseasContract?.weeklySalaryCutThrough)}원 /{' '}
							{formatNumber(itemData?.overseasContract?.weeklySalaryCutFail)}원
						</span>
					)}
				</>
			)
		} else if (itemData?.contractType === 'long_term') {
			return (
				<>
					<span className={styles.displayIcon}>장기계약</span>
					{isDomestic && (
						<span className={styles.displayIcon}>
							{payCycleLabel(itemData?.domesticContract)}
						</span>
					)}
					{!isDomestic && isOverseas && (
						<span className={styles.displayIcon}>
							{payCycleLabel(itemData?.overseasContract)}
						</span>
					)}
					{isDomestic && (
						<span className={styles.displayIcon}>
							{formatNumber(itemData?.domesticContract?.weeklySalaryCutThrough)}원 /{' '}
							{formatNumber(itemData?.domesticContract?.weeklySalaryCutFail)}원
						</span>
					)}
					{!isDomestic && isOverseas && (
						<span className={styles.displayIcon}>
							{formatNumber(itemData?.overseasContract?.weeklySalaryCutThrough)}원 /{' '}
							{formatNumber(itemData?.overseasContract?.weeklySalaryCutFail)}원
						</span>
					)}
				</>
			)
		} else if (itemData?.contractType === 'year_first_half') {
			return (
				<>
					<span className={styles.displayIcon}>기간계약 (상반기)</span>
					<span className={styles.displayIcon}>
						{itemData?.termContract?.payrollCycles === 'weekly' ? '주급' : '일시불제'}
					</span>
					<span className={styles.displayIcon}>
						{formatNumber(itemData?.termContract?.salaryAmountForPeriod)}원
					</span>
				</>
			)
		} else if (itemData?.contractType === 'year_second_half') {
			return (
				<>
					<span className={styles.displayIcon}>기간계약 (하반기)</span>
					<span className={styles.displayIcon}>
						{itemData?.termContract?.payrollCycles === 'weekly' ? '주급' : '일시불제'}
					</span>
					<span className={styles.displayIcon}>
						{formatNumber(itemData?.termContract?.salaryAmountForPeriod)}원
					</span>
				</>
			)
		} else if (itemData?.contractType === 'both') {
			return (
				<>
					<span className={styles.displayIcon}>기간계약 (상반기/하반기)</span>
					<span className={styles.displayIcon}>
						{itemData?.termContract?.payrollCycles === 'weekly' ? '주급' : '일시불제'}
					</span>
					<span className={styles.displayIcon}>
						{formatNumber(itemData?.termContract?.salaryAmountForPeriod)}원
					</span>
				</>
			)
		}
		return null
	}

	const showContractForms =
		!detailLoading && itemData?.status === 'processing' && contractFormsKey > 0
	const isPeriod =
		itemData?.contractType === 'year_first_half' ||
		itemData?.contractType === 'year_second_half' ||
		itemData?.contractType === 'both'
	const isShortOrLong =
		itemData?.contractType === 'single' || itemData?.contractType === 'long_term'

	return (
		<div className={styles.jobPostFourthPageContainer}>
			{isSuccessMessageOpen && (
				<div className={styles.successMessage}>
					<span>공고가 등록이 완료되었어요!</span>
				</div>
			)}
			<div
				className={styles.backButton}
				onClick={() => {
					sessionStorage.removeItem('editPostItem')
					sessionStorage.removeItem('timePassed')
					navigate(-1)
				}}>
				<ArrowLeftIcon className={styles.backButtonIcon} />
				<span className={styles.backButtonText}>뒤로</span>
			</div>
			<div className={styles.jobPostFourthPageHeader}>
				<div className={styles.title}>공고 수정</div>
				<button
					className={styles.preview}
					onClick={(e) => {
						e.stopPropagation()
						if (isPreviewModalOpen) {
							setIsPreviewModalOpen(false)
						} else {
							setIsPreviewModalOpen(true)
						}
					}}>
					미리보기
				</button>
			</div>
			{detailLoading && (
				<p className="px-3 text-muted small" style={{ marginBottom: 12 }}>
					불러오는 중…
				</p>
			)}
			<div className={styles.jobPostDisplayContainer}>
				<div className={styles.jobPostDisplayTitle}>{itemTitle}</div>
				<div className={styles.jobPostDisplayIconContainer}>{renderDisplayIcon()}</div>
			</div>
			<div className={styles.progressBar}></div>
			<form className={styles.writeJobPostForm}>
				<div className={styles.writeJobPostFormTitle}>공고제목 및 소개를 입력해주세요</div>
				<div className={styles.writeJobPostFormContainer}>
					<div className={styles.writeJobTitle}>공고제목</div>
					<input
						type="text"
						name="jobPostTitle"
						className={styles.writeJobTitleInput}
						placeholder="예시 : KPGA 춘천오픈 5월 단기 캐디 모집"
						maxLength={40}
						autoComplete="off"
						value={jobPostTitle}
						onChange={(e) => {
							setJobPostTitle(e.target.value)
							setJobPostTitleLength(e.target.value.length)
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								e.preventDefault()
							}
						}}
					/>
					<div className={styles.writeJobLabel}>간략소개</div>
					<textarea
						name="jobPostBrief"
						className={styles.writeJobBriefInput}
						placeholder="예 : 책임감 있고 프로 정신 투철한 캐디 구합니다. 거리 측정 능숙자 우대. 경력자 환영 (500자 이내)"
						maxLength={500}
						autoComplete="off"
						value={jobPostBrief}
						onChange={(e) => {
							setJobPostBrief(e.target.value)
							setJobPostBriefLength(e.target.value.length)
						}}
					/>
					<div className={styles.jobPostBriefLength}>
						<span className={styles.jobPostBriefLengthNumber}>
							{jobPostBriefLength}
						</span>{' '}
						/ 500
					</div>
				</div>
			</form>

			{showContractForms && isPeriod && itemData?.termContract && (
				<div style={{ marginTop: 24, padding: '0 16px' }} key={`term-${contractFormsKey}`}>
					<div className={styles.writeJobPostFormTitle} style={{ marginBottom: 12 }}>
						계약 조건 (진행 중 공고만 수정 가능 · 대회 일정은 변경되지 않습니다)
					</div>
					<GeneralContractInfoSubmitForm
						ref={generalFormRef}
						onValidationChange={noopValidation}
						isOverseas={itemData.region === 'INTERNATIONAL'}
					/>
				</div>
			)}
			{showContractForms && isShortOrLong && itemData?.domesticContract && (
				<div style={{ marginTop: 24, padding: '0 16px' }} key={`dom-${contractFormsKey}`}>
					<div className={styles.writeJobPostFormTitle} style={{ marginBottom: 12 }}>
						국내 계약 조건
					</div>
					<DomesticContractInfoSubmitForm
						ref={domesticFormRef}
						onValidationChange={noopValidation}
					/>
				</div>
			)}
			{showContractForms && isShortOrLong && itemData?.overseasContract && (
				<div style={{ marginTop: 24, padding: '0 16px' }} key={`ovs-${contractFormsKey}`}>
					<div className={styles.writeJobPostFormTitle} style={{ marginBottom: 12 }}>
						해외 계약 조건
					</div>
					<OverseasContractInfoSubmitForm
						ref={overseasFormRef}
						onValidationChange={noopValidation}
					/>
				</div>
			)}

			<div className={styles.whatsNext}>
				<div className={styles.buttons}>
					<button
						className={`${styles.nextButton} ${
							isNextButtonAvailable ? '' : styles.nextButtonDisabled
						}`}
						type="button"
						disabled={!isNextButtonAvailable}
						onClick={() => {
							onSubmit()
						}}>
						완료
					</button>
				</div>
			</div>
			{isPreviewModalOpen && (
				<div
					ref={modalRef}
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						zIndex: 1000,
						cursor: 'default',
					}}
					onClick={(e) => e.stopPropagation()}
					onMouseDown={handleMouseDown}>
					<PreviewModal
						isOpen={isPreviewModalOpen}
						onClose={() => setIsPreviewModalOpen(false)}
						writtenData={{
							jobPostTitle: jobPostTitle,
							jobPostBrief: jobPostBrief,
						}}
						data={itemData}
						timePassed={timePassed}
					/>
				</div>
			)}
		</div>
	)
}

export default PostManageEdit
